import { useCallback, useEffect, useState, useRef } from "react";
import * as S from "./styles";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaPlus } from "react-icons/fa";
import { CommonButton } from "../../common-styles";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../Redux/store";
import {
  fetchMemberShipDetails,
  updateMemberShipDetails,
  requestPayout,
  fetchRequestedPayoutDetails,
} from "../../Redux/slices/settingSlice";
import CommonLoader from "../../Components/CommonLoader";
import { formatIndianNumber } from "../../Utils";
import { message } from "antd";
import ConfirmationModal from "../../Modals/ConfirmationModal";

const Payouts = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const settings = useSelector((state: RootState) => state.setting);
  const user = useSelector((state: RootState) => state.user);
  const [confirmModal, setConfirmModal] = useState(false);

  const [upiId, setUpiId] = useState("");
  const [isAvailableUPI, setIsAvailableUPI] = useState(false);
  const [loading, setLoading] = useState(false);
  const fetchedOnce = useRef(false); // Prevents duplicate API calls

  const totalEarnings =
    Number(settings?.memberIncomeStatistic?.totalEarnings ?? 0) / 100;
  const requestedPayoutAmount =
    Number(settings?.memberIncomeStatistic?.requestedPayouts ?? 0) / 100;
  const availableForPayout =
    Number(settings?.memberIncomeStatistic?.availableForPayout) / 100 || 0;
  const minPayoutAmount = Number(
    process.env.REACT_APP_MIN_AMOUNT_RESERVED_ON_PAYOUT ?? 0
  );

  const isEligibleForPayout = availableForPayout >= minPayoutAmount;

  const getMemberDetails = useCallback(async () => {
    if (!user?.id) return;
    await dispatch(fetchMemberShipDetails(user.id));
    await dispatch(fetchRequestedPayoutDetails());
  }, [dispatch, user?.id]);

  const submitUPIId = async () => {
    if (!upiId.trim()) return;
    await dispatch(updateMemberShipDetails({ upiId }));
    getMemberDetails();
  };

  const requestWithDraw = async () => {
    if (!user?.id || loading) return;

    if (availableForPayout < minPayoutAmount) {
      message.warning(
        `You need at least ₹${minPayoutAmount} to request a payout.`
      );
      return;
    }

    setLoading(true);
    try {
      const payload = {
        creator: user.id,
        amount: availableForPayout * 100,
        status: "Requested",
        upiId,
      };

      const result = await dispatch(requestPayout(payload));
      
      // Fetch updated details after successful request
      setTimeout(() => {
        getMemberDetails();
        setLoading(false);
      }, 1500);
    } catch (error) {
      setLoading(false);
      message.error("Failed to request payout. Please try again.");
    }
  };

  const toggleConfirmModal = () => setConfirmModal(!confirmModal);

  const onConfirm = async (isConfirm: boolean) => {
    if (isConfirm) {
      toggleConfirmModal();
      await requestWithDraw();
    } else {
      toggleConfirmModal();
    }
  };

  useEffect(() => {
    if (!fetchedOnce.current && user?.id) {
      fetchedOnce.current = true;
      getMemberDetails();
    }
  }, [getMemberDetails, user?.id]);

  useEffect(() => {
    if (settings?.memberDetails?.upiId) {
      setUpiId(settings.memberDetails.upiId);
      setIsAvailableUPI(true);
    }
  }, [settings?.memberDetails?.upiId]);

  if (settings.loading)
    return (
      <S.Payouts>
        <h1 className="text-white text-center font-noto font-semibold">
          Payouts
        </h1>
        <CommonLoader message="Fetching payout details..." />
      </S.Payouts>
    );

  return (
    <S.Payouts className="text-white">
      <h1 className="text-white font-noto flex items-center gap-5 md:gap-10">
        <span onClick={() => navigate("/account")}>
          <FaArrowLeft className="cursor-pointer" size={30} />
        </span>
        <span className="font-semibold">Payouts</span>
      </h1>

      <div className="flex items-center justify-center flex-col">
        <p className="text-gray-400 text-2xl md:text-4xl sm:text-lg">
          Current Earnings
        </p>
        <p className="text-white text-2xl md:text-4xl sm:text-lg my-6">
          ₹{" "}
          {availableForPayout > 0
            ? formatIndianNumber(availableForPayout)
            : "0.00"}
        </p>

        <div className="md:w-[20rem] flex items-center justify-center flex-col">
          {settings.memberIncomeStatistic.requestedPayouts > 0 && (
            <p className="my-5 text-gray-400">
              {`Request pending ₹${" "}${formatIndianNumber(
                requestedPayoutAmount
              )}`}
            </p>
          )}
          <CommonButton
            variant={
              !isAvailableUPI ||
              settings.memberIncomeStatistic.availableForPayout === 0
                ? "primary"
                : "secondary"
            }
            onClick={toggleConfirmModal}
            disabled={
              !isAvailableUPI ||
              loading ||
              settings.memberIncomeStatistic.requestedPayouts !== 0 ||
              !isEligibleForPayout
            }
          >
            {loading ? "Requesting..." : "Request Withdrawal"}
          </CommonButton>
        </div>

        <div className="my-6 md:w-[20rem] flex items-center flex-col">
          <S.CommonInput
            className="p-15"
            placeholder="Add your UPI ID"
            name="upiId"
            value={upiId}
            onChange={(e) => setUpiId(e.target.value)}
          />
          {upiId && (
            <div
              className="flex items-center underline underline-offset-2 cursor-pointer mt-2"
              onClick={submitUPIId}
            >
              <FaPlus className="cursor-pointer" size={15} />{" "}
              {isAvailableUPI ? "Update" : "Add"}
            </div>
          )}
        </div>

        <div className="text-center text-gray-400 md:w-[30rem]">
          <p>The minimum payout amount is ₹{minPayoutAmount}.</p>
          <p>
            You will receive 80% of your total earnings, unless agreed
            otherwise.
          </p>
          <p>
            Withdrawals are processed within 10 days, unless notified
            otherwise.
          </p>
        </div>
      </div>
      <ConfirmationModal
        onHide={() => toggleConfirmModal()}
        show={confirmModal}
        setConfirm={(isConfirm) => onConfirm(isConfirm)}
        confirmButtonText={"Confirm"}
      />
    </S.Payouts>
  );
};

export default Payouts;
